/**
 * usePageTracking — Automatic page-view tracking on route changes.
 *
 * Place this hook once in AppShell to cover the entire webapp.
 * Maps route patterns → route_group for clean analytics aggregation.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { trackPageView } from '../lib/tracking/tracking-client';

// ─────────────────────────────────────────────────────────────────────────────
// Route → route_group mapping
// ─────────────────────────────────────────────────────────────────────────────

type RouteGroup =
    | 'memorial'
    | 'channel'
    | 'home'
    | 'search'
    | 'atag'
    | 'chat'
    | 'profile'
    | 'settings'
    | 'auth'
    | 'partner'
    | 'candle'
    | 'landing'
    | 'other';

function classifyRoute(pathname: string): { route_group: RouteGroup; page_id: string | null } {
    // Memorial pages — extract page_id
    const memorialMatch = pathname.match(/^\/memorial\/([a-f0-9-]{36})/i);
    if (memorialMatch) {
        return { route_group: 'memorial', page_id: memorialMatch[1] };
    }

    if (pathname.startsWith('/channels')) return { route_group: 'channel', page_id: null };
    if (pathname === '/home') return { route_group: 'home', page_id: null };
    if (pathname.startsWith('/search')) return { route_group: 'search', page_id: null };
    if (pathname.startsWith('/atag')) return { route_group: 'atag', page_id: null };
    if (pathname.startsWith('/chat')) return { route_group: 'chat', page_id: null };
    if (pathname.startsWith('/profile') || pathname.startsWith('/account')) return { route_group: 'profile', page_id: null };
    if (pathname.startsWith('/settings')) return { route_group: 'settings', page_id: null };
    if (pathname.startsWith('/my-pages')) return { route_group: 'profile', page_id: null };
    if (pathname.startsWith('/light-a-candle')) return { route_group: 'candle', page_id: null };
    if (
        pathname.startsWith('/sign-in') ||
        pathname.startsWith('/sign-up') ||
        pathname.startsWith('/verify') ||
        pathname.startsWith('/forgot') ||
        pathname.startsWith('/reset') ||
        pathname.startsWith('/auth') ||
        pathname.startsWith('/invite')
    ) {
        return { route_group: 'auth', page_id: null };
    }
    if (pathname.startsWith('/partner')) return { route_group: 'partner', page_id: null };
    if (pathname === '/' || pathname === '/honored') return { route_group: 'landing', page_id: null };

    return { route_group: 'other', page_id: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePageTracking(): void {
    const location = useLocation();
    const lastTrackedRef = useRef<string>('');

    useEffect(() => {
        const pathname = location.pathname;

        // Skip if exactly the same path (avoiding StrictMode double-fire)
        if (pathname === lastTrackedRef.current) return;
        lastTrackedRef.current = pathname;

        const { route_group, page_id } = classifyRoute(pathname);

        trackPageView({
            route: pathname,
            route_group,
            page_id,
        });
    }, [location.pathname]);
}
